// Validators métier centralisés (FR / Côte d'Ivoire)
// Conventions :
// - "rule*" → renvoie un objet de règle AntD utilisable dans Form.Item rules={[...]}
// - "is*"   → renvoie un booléen utilitaire
// - Tous les messages sont rédigés directement (pas de "Requis" laconique)

// ─── Téléphone CI ────────────────────────────────────────────────────────
// 10 chiffres après l'indicatif +225. Première paire = 01-09 (mobile) ou 21-27 (fixe).
export const isPhoneCI = (val) => /^[0-9]{10}$/.test(String(val || '').replace(/\D/g, ''));

export const rulePhoneCI = ({ required = true, label = 'téléphone' } = {}) => ([
    ...(required ? [{ required: true, message: `Le ${label} est obligatoire` }] : []),
    { pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir 10 chiffres (sans indicatif)' },
]);

// ─── Email ───────────────────────────────────────────────────────────────
export const ruleEmail = ({ required = false } = {}) => ([
    ...(required ? [{ required: true, message: 'L\'adresse email est obligatoire' }] : []),
    { type: 'email', message: "Format d'email invalide (ex : nom@domaine.ci)" },
]);

// ─── Montant FCFA ────────────────────────────────────────────────────────
export const ruleMontant = ({ required = true, min = 0, max, label = 'montant', strict = false } = {}) => {
    const rules = [];
    if (required) rules.push({ required: true, message: `Le ${label} est obligatoire` });
    rules.push({
        validator: (_, v) => {
            if (v == null || v === '') return Promise.resolve();
            const num = Number(v);
            if (Number.isNaN(num)) return Promise.reject(new Error(`Le ${label} doit être un nombre`));
            if (strict ? num <= min : num < min) {
                return Promise.reject(new Error(`Le ${label} doit être ${strict ? 'supérieur' : 'au moins égal'} à ${min.toLocaleString('fr-FR')}`));
            }
            if (max != null && num > max) {
                return Promise.reject(new Error(`Le ${label} ne peut pas dépasser ${max.toLocaleString('fr-FR')}`));
            }
            return Promise.resolve();
        },
    });
    return rules;
};

// ─── Texte requis ────────────────────────────────────────────────────────
export const ruleRequired = (label = 'champ') => ([
    { required: true, message: `Le ${label} est obligatoire` },
]);

// ─── Numéro de police / attestation ─────────────────────────────────────
export const ruleNumeroPolice = ({ required = true } = {}) => ([
    ...(required ? [{ required: true, message: 'Le numéro de police est obligatoire' }] : []),
    { min: 3, message: 'Le numéro de police doit contenir au moins 3 caractères' },
    { max: 30, message: 'Le numéro de police ne peut pas dépasser 30 caractères' },
]);

// ─── Dates métier (utilise dayjs/moment-like : .isAfter, .isBefore, .toDate) ────
const toMs = (d) => {
    if (!d) return null;
    if (typeof d.valueOf === 'function') return d.valueOf();
    return new Date(d).getTime();
};

// Date d'échéance > date d'effet
export const validatorDateApresEffet = (effetFieldName = 'dateEffet') => ({ getFieldValue }) => ({
    validator(_, value) {
        const effet = getFieldValue(effetFieldName);
        if (!value || !effet) return Promise.resolve();
        if (toMs(value) <= toMs(effet)) {
            return Promise.reject(new Error("La date d'échéance doit être postérieure à la date d'effet"));
        }
        return Promise.resolve();
    },
});

// Date non future (ex : date de naissance, date de sinistre)
export const ruleDateNonFuture = ({ label = 'date' } = {}) => ([{
    validator(_, value) {
        if (!value) return Promise.resolve();
        if (toMs(value) > Date.now()) {
            return Promise.reject(new Error(`La ${label} ne peut pas être dans le futur`));
        }
        return Promise.resolve();
    },
}]);

// Date dans une plage (par défaut : entre dateEffet et aujourd'hui)
export const validatorDateSinistre = (effetFieldName = 'dateEffet') => ({ getFieldValue }) => ({
    validator(_, value) {
        if (!value) return Promise.resolve();
        if (toMs(value) > Date.now()) {
            return Promise.reject(new Error('La date du sinistre ne peut pas être dans le futur'));
        }
        const effet = getFieldValue(effetFieldName);
        if (effet && toMs(value) < toMs(effet)) {
            return Promise.reject(new Error("La date du sinistre doit être postérieure à la date d'effet du contrat"));
        }
        return Promise.resolve();
    },
});

// Date de naissance : majorité (>= 18 ans)
export const ruleDateNaissanceMajeur = () => ([{
    validator(_, value) {
        if (!value) return Promise.resolve();
        const ms = toMs(value);
        if (ms > Date.now()) {
            return Promise.reject(new Error('La date de naissance ne peut pas être dans le futur'));
        }
        const ageMs = Date.now() - ms;
        const eighteenYears = 18 * 365.25 * 24 * 3600 * 1000;
        if (ageMs < eighteenYears) {
            return Promise.reject(new Error("L'assuré doit être majeur (au moins 18 ans)"));
        }
        return Promise.resolve();
    },
}]);

// ─── Normalisations ──────────────────────────────────────────────────────
export const normalizeUpper = (v) => (v ? String(v).toUpperCase() : v);
export const normalizeLowerTrim = (v) => (v ? String(v).toLowerCase().trim() : v);
export const normalizeDigitsOnly = (v) => (v ? String(v).replace(/\D/g, '') : v);
